import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import Tooltip from 'ui-library/bs/Tooltip';
import FieldError from 'ui-library/FieldError';
import useThrottle from 'ui-library/hooks/useThrottle';
import WarningIcon from 'ui-library/icons/WarningIcon';
import {IntermediateValueControlledNumericInput} from 'ui-library/inputs/ControlledNumericInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';

interface YAxisRangeParameters {
  yAxisMin: number | null;
  yAxisMax: number | null;
}

export interface YAxisRangeConfigEditorProps {
  onUpdateParameters: (update: YAxisRangeParameters) => void;
  parameters: YAxisRangeParameters;
  warning?: string | null;
}

/** @package */
export const THROTTLE_INTERVAL = 300;

export default function YAxisRangeConfigEditor({
  onUpdateParameters,
  parameters,
  warning,
}: YAxisRangeConfigEditorProps): JSX.Element {
  const [draftMin, setDraftMin] = useState<number | null>(parameters.yAxisMin);
  const [draftMax, setDraftMax] = useState<number | null>(parameters.yAxisMax);

  function handleOnChangeMin(newMin: number | null): void {
    setDraftMin(newMin);
  }

  function handleOnChangeMax(newMax: number | null): void {
    setDraftMax(newMax);
  }

  const throttledDraftMin = useThrottle(draftMin, THROTTLE_INTERVAL);
  const throttledDraftMax = useThrottle(draftMax, THROTTLE_INTERVAL);

  useEffect(() => {
    // If one of the parameter values has changed
    if (parameters.yAxisMin !== throttledDraftMin || parameters.yAxisMax !== throttledDraftMax) {
      // If the parameter values are valid (i.e., either one or more value is null, or else min < max),
      // then update the parameters
      if (throttledDraftMin == null || throttledDraftMax == null || throttledDraftMin < throttledDraftMax) {
        onUpdateParameters({yAxisMin: throttledDraftMin, yAxisMax: throttledDraftMax});
      }
    }
  }, [onUpdateParameters, parameters, throttledDraftMin, throttledDraftMax]);

  return (
    <div className="u-input-spacing-top">
      <FieldLabel
        label={
          <>
            Y-Axis Range
            {warning && (
              <Tooltip title={warning}>
                <WarningIcon className="u-tiny-spacing-left" />
              </Tooltip>
            )}
          </>
        }
      />

      <div className={css(styles.numericInputsWrapper)}>
        <IntermediateValueControlledNumericInput
          className="u-mini-spacing-left"
          onChange={(e) => {
            handleOnChangeMin(e.target.numericValue);
          }}
          placeholder="Min"
          value={draftMin}
        />
        <IntermediateValueControlledNumericInput
          className="u-mini-spacing-left"
          onChange={(e) => {
            handleOnChangeMax(e.target.numericValue);
          }}
          placeholder="Max"
          value={draftMax}
        />
      </div>
      {draftMin != null && draftMax != null && draftMax <= draftMin && (
        <FieldError message={`Max needs to be greater than ${draftMin}`} />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  numericInputsWrapper: {
    display: 'flex',
  },
});
